import type { SolvedParallelogram } from './solver.model';

/**
 * Solve a parallelogram given base, side, and included angle.
 * Returns all properties and vertices in canonical placement.
 *
 * Canonical placement: A at origin, base along +x.
 * A = (0,0), B = (b,0), C = (b + s*cos(th), s*sin(th)), D = (s*cos(th), s*sin(th))
 * where th is the included angle at A.
 */
export function solveParallelogram(values: Partial<Record<'b' | 's' | 'angle', number>>): SolvedParallelogram {
  const b = values.b!;
  const s = values.s!;
  const angleDeg = values.angle!;

  if (b === undefined || s === undefined || angleDeg === undefined) {
    throw new Error('Parallelogram BSA format requires b, s, and angle');
  }
  if (b <= 0 || s <= 0) {
    throw new Error(`Base and side must be positive: b=${b}, s=${s}`);
  }
  if (angleDeg <= 0 || angleDeg >= 180) {
    throw new Error(`Angle must be between 0° and 180°: angle=${angleDeg}`);
  }

  const angleRad = (angleDeg * Math.PI) / 180;
  const h = s * Math.sin(angleRad);
  const offset = s * Math.cos(angleRad);

  const A: [number, number] = [0, 0];
  const B: [number, number] = [b, 0];
  const C: [number, number] = [b + offset, h];
  const D: [number, number] = [offset, h];

  const d1 = Math.sqrt((C[0] - A[0]) ** 2 + (C[1] - A[1]) ** 2);
  const d2 = Math.sqrt((D[0] - B[0]) ** 2 + (D[1] - B[1]) ** 2);

  return {
    vertices: { A, B, C, D },
    b,
    s,
    angle: angleDeg,
    h,
    d1,
    d2,
    area: b * h,
    perimeter: 2 * (b + s),
  };
}
